import { motion } from 'motion/react';
import { LoadingSpinner } from '@figma/fpl-components';
import { useCallback, type ReactNode } from 'react';
import { FF_FPL_MIGRATION } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { snapSpinnerSize } from '@/UI/CircularProgress/CircularProgress';

/**
 * FPL-native configuration for the `fpl-migration`-on branch. Callers pass it without checking the
 * flag; the legacy SVG branch ignores the entire bucket.
 */
export interface LoadingCircleFplProps {
  /** Screen-reader announcement text; FPL defaults to "Loading" when omitted. */
  loadingText?: ReactNode;
}

/**
 * Re-anchors FPL's spinner rotation to the document timeline, so two spinners that mounted at
 * different moments (the run button and a hover tooltip) sit at the same angle instead of a fixed
 * offset apart. FPL hard-codes `1s linear infinite` on the glyph, so the phase is the only thing
 * left to align, and `startTime = 0` is the one value every instance can agree on.
 */
const useSyncedSpinnerPhase = () =>
  useCallback((root: HTMLDivElement | null) => {
    if (!root?.getAnimations) return;
    const anchor = () => {
      for (const animation of root.getAnimations({ subtree: true })) {
        animation.startTime = 0;
      }
    };
    anchor();
    // A CSS animation is not always registered yet in the commit phase, so catch it on the next frame too.
    requestAnimationFrame(anchor);
  }, []);

export interface LoadingCircleProps {
  size?: number;
  strokeWidth?: number;
  color?: string;
  /** Configuration read only by the `fpl-migration`-on branch; ignored by the legacy branch. */
  fplProps?: LoadingCircleFplProps;
}

/**
 * Indeterminate circular spinner and the `fpl-migration` boundary. (`figma-theme` owns coloring
 * only; the FPL-vs-legacy swap is gated separately by `fpl-migration`.)
 *
 * - Flag off: the legacy bespoke `motion/react` SVG, unchanged (`size`, `strokeWidth`, `color`).
 * - Flag on: FPL's `LoadingSpinner` with FPL's native color. `size` snaps to the nearest FPL preset
 *   (default `sm`/16px). `color` and `strokeWidth` are legacy-only — the flag-on spinner takes no
 *   color override.
 */
export const LoadingCircle = ({
  size = 16,
  strokeWidth = 0.753333,
  color = 'currentColor',
  fplProps,
}: LoadingCircleProps) => {
  const { value: isFplMigration } = useFeatureFlag(FF_FPL_MIGRATION);
  const syncSpinnerPhase = useSyncedSpinnerPhase();

  if (isFplMigration) {
    return (
      <LoadingSpinner ref={syncSpinnerPhase} size={snapSpinnerSize(size, 'sm')} loadingText={fplProps?.loadingText} />
    );
  }

  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <motion.path
        d="M14.2009 9.17644C14.0003 10.3138 13.479 11.3702 12.6983 12.2213C11.9176 13.0724 10.9101 13.6828 9.79421 13.9807C8.25714 14.3925 6.61941 14.1769 5.2413 13.3813C3.8632 12.5856 2.8576 11.2751 2.44575 9.73804C2.03389 8.20097 2.2495 6.56323 3.04515 5.18513C3.8408 3.80702 5.15131 2.80143 6.68839 2.38957C7.80368 2.08961 8.98144 2.11446 10.0831 2.4612C11.1847 2.80794 12.1644 3.46214 12.9068 4.34681"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        animate={{
          rotate: 360,
        }}
        transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear',
        }}
        style={{
          transformOrigin: 'center',
        }}
      />
    </svg>
  );
};
